/*
 * REVENGE GLOBE v6 - three.js (r170 / 0.170.0)                                   FINAL - DO NOT CHANGE
 * ==================================================================================================
 * A spinning, draggable 3D planet. Everything (final settings + styling) is built into this file.
 *
 * FILES (keep all four in the same folder, with these exact names):
 *   revenge-globe-v6.js             <- this file
 *   planet-v6-texture-4k.jpg        planet surface (desktop)
 *   planet-v6-texture-2k.jpg        planet surface (phones)
 *   planet-v6-height-2k.jpg         surface depth (cracks / craters)
 *
 * TO USE - add this one line to the page (end of <body>):
 *
 *   <script type="module" src="/path/to/revenge-globe-v6.js"></script>
 *
 * That's it. The globe fills the screen BEHIND the page content (fixed, z-index -1). For it to show,
 * the page background must be on <html> (e.g. html { background: #000 }) and <body> / sections that
 * cover it must be transparent - a body background colour paints over the globe.
 *
 * Optional: to place it yourself instead, add  <div class="rg-planet-v6 rg-planet-v6--background"></div>
 * where you want it; the script uses that element instead of creating one.
 *
 * Notes: three.js is loaded from https://cdn.jsdelivr.net/npm/three@0.170.0 (no npm / build step).
 * Visitors can only drag (or swipe sideways) to spin it - there are no visitor controls.
 * Images must be served from the same site (or with CORS headers). Opening the HTML file straight from
 * disk blocks the images - test on a real server or e.g. "npx serve".
 */
// Revenge Globe: a realistic cracked, burning planet for Shopify (three.js).
// The surface is generated on the GPU once at load (no image files), then lit and spun cheaply.
// Starts every .rg-planet-v6 element on the page; settings come from its data-* attributes.
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.min.js';

const NS = '__revengePlanetV6';

/* ------------------------------------------------------------------ manager */

function createManager() {
  THREE.ColorManagement.enabled = false;
  const instances = new Map();
  function initAll(root) {
    (root || document).querySelectorAll('.rg-planet-v6').forEach((el) => {
      if (!instances.has(el)) instances.set(el, createPlanet(el));
    });
  }
  function disposeEl(el) {
    const inst = instances.get(el);
    if (inst) { inst.dispose(); instances.delete(el); }
  }
  function disposeSection(id) {
    for (const el of [...instances.keys()]) if (el.dataset.sectionId === String(id)) { disposeEl(el); el.remove(); }
  }
  // Shopify theme editor re-renders sections without re-running their scripts.
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => disposeSection(e.detail && e.detail.sectionId));
  return { initAll, disposeEl, disposeSection };
}

function hashString(s) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ------------------------------------------------------------------ one planet */

function createPlanet(el) {
  const d = el.dataset;
  const num = (v, def) => { const n = parseFloat(v); return Number.isFinite(n) ? n : def; };
  const opt = {
    size: num(d.size, 80) / 100,
    offsetY: num(d.offsetY, 0) / 100,
    lava: num(d.lava, 50) / 100,
    fire: num(d.fire, 100) / 100,
    flares: d.flares !== 'false',
    pulse: num(d.pulse, 60) / 100,     // how much the lava's colour and brightness pulse
    exposure: num(d.brightness, 100) / 100,
    quality: ['low', 'high'].includes(d.quality) ? d.quality : 'auto',
    speed: num(d.speed, 20),
    tilt: (num(d.tilt, 14) * Math.PI) / 180,
    drag: d.drag !== 'false',
    seed: (d.seed || '').trim() || String(Math.random()),
    tex: d.tex || '',            // equirectangular (2:1) planet texture; empty = generated surface
    texMobile: d.texMobile || '',
    height: d.height || '',      // matching height map (white = high); gives the cracks real depth
    clouds: d.clouds !== 'false',
    cloudAmount: num(d.cloudAmount, 45) / 100,
    cloudDrift: num(d.cloudDrift, 20) / 100,  // how much faster than the planet the clouds move
    atmosphere: (num(d.atmosphere, 50) / 100) * 1.2,   // v5: glow 20% stronger at every slider setting
    startYaw: d.yaw !== undefined && d.yaw !== '' ? (num(d.yaw, 0) * Math.PI) / 180 : null,
    lighting: d.lighting === 'soft' ? 'soft' : 'sun',   // sun = one fixed light top-right, dark side bottom-left
    darkSide: num(d.darkSide, 4) / 100,                 // how much light reaches the dark side (sun lighting)
  };
  const background = el.classList.contains('rg-planet-v6--background');
  const reduceMotion = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const coarse = window.matchMedia && matchMedia('(pointer: coarse)').matches;
  if (background && el.parentElement !== document.body) document.body.prepend(el);

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
  } catch (err) {
    el.classList.add('rg-planet-v6--no-webgl');
    return { dispose() {} };
  }
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  renderer.setClearColor(0x000000, 0);
  const canvas = renderer.domElement;
  canvas.className = 'rg-planet-v6__canvas';
  canvas.setAttribute('aria-hidden', 'true');
  el.appendChild(canvas);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(30, 1, 0.05, 100);
  const tiltG = new THREE.Group();
  const spinG = new THREE.Group();
  tiltG.add(spinG);
  scene.add(tiltG);
  const disposables = [];

  // Lighting, shared by the planet, clouds and atmosphere. The light is fixed to the camera, so the lit
  // side stays put while the planet spins or is dragged underneath it.
  const sunMode = opt.lighting === 'sun';
  const sun = {
    uLightDir: { value: sunMode ? new THREE.Vector3(0.62, 0.57, 0.32).normalize() : new THREE.Vector3(-0.5, 0.72, 0.48).normalize() },
    uKey: { value: sunMode ? 19.0 : 5.2 },             // main light strength
    uFill: { value: sunMode ? 0.0 : 1.0 },             // the old soft fill + back lights
    uAmbient: { value: sunMode ? opt.darkSide : 0.22 },// light on the dark side
    uWrap: { value: sunMode ? 0.06 : 0.3 },            // how far light wraps past the terminator (clouds)
    uRimBase: { value: sunMode ? 0.02 : 0.2 },         // atmosphere brightness on the dark side
  };

  /* ---------------- surface bake (cube maps, generated once) */
  const dprCap = Math.min(window.devicePixelRatio || 1, 2);
  function faceSizeFor(diameterCss) {
    if (opt.quality === 'low') return 384;
    const want = diameterCss * dprCap * 0.75;
    const cap = opt.quality === 'high' ? 1024 : coarse ? 640 : 896;
    return Math.max(256, Math.min(cap, Math.round(want / 128) * 128));
  }
  const gl2Float = renderer.extensions.has('EXT_color_buffer_float') || renderer.extensions.has('EXT_color_buffer_half_float');
  const seedRand = mulberry32(hashString(opt.seed));
  const seedVec = new THREE.Vector3(seedRand() * 97, seedRand() * 97, seedRand() * 97);

  let useTexture = !!(opt.tex || opt.texMobile);
  let ready = false;
  let disposed = false;
  let emberScale = 1;
  const clamp01 = (v) => Math.min(1, Math.max(0, v));
  let bake = null; // { rtA, rtB, size, pending: [...] }
  const bakeMat = new THREE.ShaderMaterial({
    uniforms: { uMode: { value: 0 }, uSeed: { value: seedVec }, uLava: { value: opt.lava } },
    vertexShader: 'varying vec3 vP; void main() { vP = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    fragmentShader: NOISE_GLSL + BAKE_FS,
    side: THREE.BackSide,
    depthTest: false,
    depthWrite: false,
  });
  const bakeScene = new THREE.Scene();
  const bakeBox = new THREE.Mesh(new THREE.BoxGeometry(2, 2, 2), bakeMat);
  bakeScene.add(bakeBox);
  disposables.push(bakeMat, bakeBox.geometry);

  function startBake(size) {
    if (bake && size <= bake.size) return; // only re-generate when the planet gets noticeably bigger
    if (bake) { bake.rtA.dispose(); bake.rtB.dispose(); }
    const common = { generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false };
    const rtA = new THREE.WebGLCubeRenderTarget(size, Object.assign({ type: THREE.UnsignedByteType }, common));
    const rtB = new THREE.WebGLCubeRenderTarget(size, Object.assign({ type: gl2Float ? THREE.HalfFloatType : THREE.UnsignedByteType }, common));
    const cubeCam = new THREE.CubeCamera(0.1, 10, rtA);
    bake = { rtA, rtB, size, cubeCam, pending: [[rtA, 0], [rtB, 1]], done: false };
    planetMat.uniforms.tAlb.value = rtA.texture;
    planetMat.uniforms.tHgt.value = rtB.texture;
    planetMat.uniforms.uTexel.value = 3.2 / size;
  }
  function stepBake() {
    // one cube map per frame so a slow phone never stalls for long
    if (!bake || !bake.pending.length) return false;
    const [rt, mode] = bake.pending.shift();
    bakeMat.uniforms.uMode.value = mode;
    bake.cubeCam.renderTarget = rt;
    bake.cubeCam.update(renderer, bakeScene);
    if (!bake.pending.length) { bake.done = true; ready = true; if (!bornAt) bornAt = performance.now(); }
    return true;
  }

  /* ---------------- planet */
  const planetMat = new THREE.ShaderMaterial({
    uniforms: {
      tAlb: { value: null }, tHgt: { value: null }, uTexel: { value: 0.005 },
      uDisp: { value: 0.032 }, uBump: { value: 1.25 }, uExposure: { value: opt.exposure },
      uFire: { value: opt.fire }, uTime: { value: 0 }, uIntro: { value: 0 },
    },
    vertexShader: PLANET_VS,
    fragmentShader: NOISE_GLSL + PLANET_FS,
    transparent: false,
  });
  const segs = coarse ? [192, 128] : [288, 192];
  const planetGeo = new THREE.SphereGeometry(1, segs[0], segs[1]);
  const planet = new THREE.Mesh(planetGeo, planetMat);
  planet.visible = false;
  spinG.add(planet);
  disposables.push(planetMat, planetGeo);

  /* ---------------- textured planet (photo texture wrapped round the sphere) */
  // Depth: real crevices from a height map (white = plate tops, black = deepest cracks).
  // Desktop marches the view ray through it (parallax occlusion) and toward the light (self-shadow);
  // phones keep the self-shadow and height normals but skip the parallax march.
  const texMat = new THREE.ShaderMaterial({
    uniforms: {
      tMap: { value: null }, tHgt: { value: null }, uHasHgt: { value: 0 },
      uHgtTexel: { value: new THREE.Vector2(1 / 2048, 1 / 1024) }, uHgtSize: { value: new THREE.Vector2(2048, 1024) },
      uDepth: { value: 0.016 }, uPomMax: { value: coarse ? 0 : 22 }, uPomMin: { value: coarse ? 0 : 8 },
      uShadowSteps: { value: coarse ? 6 : 10 }, uExposure: { value: opt.exposure }, uPulse: { value: opt.pulse },
      uLightDir: sun.uLightDir, uKey: sun.uKey, uFill: sun.uFill, uAmbient: sun.uAmbient,
      uLavaGain: { value: 1 },   // set from the texture once it loads: bright, saturated lava gets less boost
      uFire: { value: opt.fire }, uTime: { value: 0 }, uIntro: { value: 0 },
    },
    vertexShader: TEX_VS,
    fragmentShader: NOISE_GLSL + TEX_FS,
  });
  disposables.push(texMat);
  if (useTexture) {
    // texture width needed for sharp detail ~ 2*pi * globe radius in device pixels; phones always get the light copy
    const box = el.getBoundingClientRect();
    const needed = 2 * Math.PI * ((opt.size * Math.min(box.width || innerWidth, box.height || innerHeight)) / 2) * dprCap;
    const small = coarse || needed <= 2400;
    const url = (small && opt.texMobile) || opt.tex || opt.texMobile;
    const loader = new THREE.TextureLoader();
    const setup = (tex) => {
      tex.wrapS = THREE.RepeatWrapping;               // the left and right edges join up
      tex.wrapT = THREE.ClampToEdgeWrapping;
      tex.colorSpace = THREE.NoColorSpace;
      disposables.push(tex);
      return tex;
    };
    const colorP = new Promise((res, rej) => loader.load(url, res, undefined, rej));
    const heightP = opt.height ? new Promise((res) => loader.load(opt.height, res, undefined, () => res(null))) : Promise.resolve(null);
    Promise.all([colorP, heightP]).then(([tex, hgt]) => {
      if (disposed) { tex.dispose(); if (hgt) hgt.dispose(); return; }
      setup(tex).anisotropy = renderer.capabilities.getMaxAnisotropy();
      texMat.uniforms.tMap.value = tex;
      if (hgt) {
        setup(hgt);
        texMat.uniforms.tHgt.value = hgt;
        texMat.uniforms.uHasHgt.value = 1;
        texMat.uniforms.uHgtTexel.value.set(1 / hgt.image.width, 1 / hgt.image.height);
        texMat.uniforms.uHgtSize.value.set(hgt.image.width, hgt.image.height);
      } else {
        texMat.uniforms.tHgt.value = tex;          // no height map: fall back to the colour's brightness
        texMat.uniforms.uHgtTexel.value.set(1 / tex.image.width, 1 / tex.image.height);
        texMat.uniforms.uHgtSize.value.set(tex.image.width, tex.image.height);
        texMat.uniforms.uDepth.value = 0.006;
      }
      planet.material = texMat;
      const fire = fireFromImage(tex.image);
      if (fire) makeEmbers(fire.dir, fire.spread, fire.tint);
      // lava that is already bright and pinkish would bleach to white if boosted like deep-red lava
      if (fire) texMat.uniforms.uLavaGain.value = Math.min(1, Math.max(0.35, 1 - (fire.wash - 0.215) * 5.5));
      if (window.__rgDebugLog && fire) console.log('rg lava wash ' + fire.wash.toFixed(3) + ' gain ' + texMat.uniforms.uLavaGain.value.toFixed(2));
      if (opt.startYaw !== null) st.yaw = opt.startYaw;    // 0 = the middle of the map's left half faces the viewer
      else if (fire) st.yaw = -0.35 - Math.atan2(fire.dir.x, fire.dir.z);  // fire facing the viewer, left of centre
      ready = true;
      bornAt = window.__rgNoFade ? 1 : performance.now();
      if (window.__rgDebugLog) console.log('rg texture ' + tex.image.width + 'x' + tex.image.height + (hgt ? ' + height ' + hgt.image.width : ' no height') + (fire ? ' fire found' : ' no fire'));
    }).catch(() => {
      // texture missing or blocked: fall back to the generated surface
      if (disposed) return;
      useTexture = false;
      makeEmbers(new THREE.Vector3(-0.5, -0.45, 0.74).normalize(), 0.18 + 0.3 * opt.lava);
      layout();
    });
  }

  // find where the fire is in the texture (orange pixels) so the embers rise from it
  function fireFromImage(image) {
    const w = 256, h = 128;
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    const g = cv.getContext('2d', { willReadFrequently: true });
    let px;
    try { g.drawImage(image, 0, 0, w, h); px = g.getImageData(0, 0, w, h).data; } catch (err) { return null; }
    // fire weight per pixel (area-corrected), favouring solid lava over thin glowing cracks
    const mass = new Float32Array(w * h);
    const dirOf = (x, y) => {
      const th = ((y + 0.5) / h) * Math.PI, ph = ((x + 0.5) / w) * Math.PI * 2;
      return [-Math.cos(ph) * Math.sin(th), Math.cos(th), Math.sin(ph) * Math.sin(th)];
    };
    for (let y = 0; y < h; y++) {
      const area = Math.sin(((y + 0.5) / h) * Math.PI);
      for (let x = 0; x < w; x++) {
        const i = (y * w + x) * 4, r = px[i] / 255, b = px[i + 2] / 255;
        const m = clamp01((r - b - 0.15) / 0.2) * clamp01((r - 0.3) / 0.25);
        mass[y * w + x] = m * m * area;
      }
    }
    // the biggest burning area wins (so a second fire on the far side doesn't pull the embers off target)
    const C = 16;
    let best = 0, bx = 0, by = 0;
    for (let cy = 0; cy < h / C; cy++) for (let cx = 0; cx < w / C; cx++) {
      let s = 0;
      for (let y = cy * C; y < (cy + 1) * C; y++) for (let x = cx * C; x < (cx + 1) * C; x++) s += mass[y * w + x];
      if (s > best) { best = s; bx = cx; by = cy; }
    }
    if (best <= 0) return null;
    const c0 = dirOf(bx * C + C / 2, by * C + C / 2);
    let sx = 0, sy = 0, sz = 0, n = 0, cr = 0, cg = 0, cb = 0;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const m = mass[y * w + x];
      if (m <= 0) continue;
      const p = dirOf(x, y);
      if (p[0] * c0[0] + p[1] * c0[1] + p[2] * c0[2] < 0.75) continue;   // within ~40 degrees
      sx += p[0] * m; sy += p[1] * m; sz += p[2] * m; n += m;
      const i = (y * w + x) * 4;
      cr += px[i] * m; cg += px[i + 1] * m; cb += px[i + 2] * m;
    }
    if (n < 4) return null;
    const mx = Math.max(cr, cg, cb, 1e-6);
    const tint = new THREE.Vector3(cr / mx, cg / mx, cb / mx);   // the lava's own colour, so flares match it
    const len = Math.hypot(sx, sy, sz);
    // how washed-out (white/pink) the lava already is: green+blue relative to red. Deep reds ~0.15, pinkish-white ~0.6
    const wash = (cg + cb) / (2 * Math.max(cr, 1e-6));
    return { dir: new THREE.Vector3(sx / len, sy / len, sz / len), spread: Math.min(0.5, Math.max(0.12, Math.acos(Math.min(1, len / n)) * 0.8)), tint, wash };
  }

  /* ---------------- solar flares: plasma loops that rise out of the lava, arc over and fall back.
     Each loop lives for several seconds (grow, glow, fade); now and then one erupts and lifts off into space.
     Sparks stream along the loops. Everything is animated in the vertex shader, so the CPU does nothing per frame. */
  let flares = null;
  const flareRes = { value: new THREE.Vector2(1, 1) };
  function makeEmbers(L, spread, tint) {
    if (flares || !opt.flares || opt.fire <= 0 || (!useTexture && opt.lava <= 0)) return;
    const n = coarse ? 4 : 6, SEG = 40, SPK = coarse ? 10 : 16;
    const fr = mulberry32(hashString(opt.seed + ':flares'));
    const up = Math.abs(L.y) < 0.95 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const t1 = new THREE.Vector3().crossVectors(up, L).normalize();
    const t2 = new THREE.Vector3().crossVectors(L, t1);
    const loops = [];
    for (let i = 0; i < n; i++) {
      const r = Math.sqrt(fr()) * spread * 0.85, a = fr() * Math.PI * 2;
      const c = L.clone().addScaledVector(t1, Math.cos(a) * r).addScaledVector(t2, Math.sin(a) * r).normalize();
      const ta = fr() * Math.PI * 2;
      const dir = t1.clone().multiplyScalar(Math.cos(ta)).addScaledVector(t2, Math.sin(ta));
      dir.addScaledVector(c, -dir.dot(c)).normalize();
      const half = 0.04 + fr() * 0.12;                       // half the distance between the loop's two feet (radians)
      loops.push({
        A: c.clone().addScaledVector(dir, -Math.tan(half)).normalize(),
        B: c.clone().addScaledVector(dir, Math.tan(half)).normalize(),
        P: [half * (0.6 + fr() * 1.2), 7 + fr() * 9, fr(), i === 0 || fr() < 0.15 ? 1 : 0],   // height, period, phase, eruptive
        W: 0.024 + fr() * 0.016,
      });
    }
    // ribbons: each loop is a bundle of 3 soft plasma strands (the middle one brightest)
    const STRANDS = [0, -1, 1];
    const nv = n * STRANDS.length * (SEG + 1) * 2;
    const pos = new Float32Array(nv * 3), aA = new Float32Array(nv * 3), aB = new Float32Array(nv * 3);
    const aP = new Float32Array(nv * 4), aT = new Float32Array(nv * 4), idx = [];
    let v = 0;
    loops.forEach((lp) => {
      for (const strand of STRANDS) {
        const w = strand === 0 ? lp.W : lp.W * (0.45 + fr() * 0.25);
        for (let s = 0; s <= SEG; s++) for (const side of [-1, 1]) {
          aA.set([lp.A.x, lp.A.y, lp.A.z], v * 3); aB.set([lp.B.x, lp.B.y, lp.B.z], v * 3);
          aP.set(lp.P, v * 4); aT.set([s / SEG, side, w, strand], v * 4);
          if (s < SEG && side === 1) { const b = v - 1; idx.push(b, b + 1, b + 2, b + 2, b + 1, b + 3); }
          v++;
        }
      }
    });
    const rg = new THREE.BufferGeometry();
    rg.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    rg.setAttribute('aA', new THREE.BufferAttribute(aA, 3));
    rg.setAttribute('aB', new THREE.BufferAttribute(aB, 3));
    rg.setAttribute('aP', new THREE.BufferAttribute(aP, 4));
    rg.setAttribute('aT', new THREE.BufferAttribute(aT, 4));
    rg.setIndex(idx);
    const common = {
      uTime: { value: 0 }, uScale: { value: emberScale }, uFire: { value: opt.fire }, uIntro: { value: 0 },
      uTint: { value: tint || new THREE.Vector3(1.0, 0.3, 0.05) },
    };
    const rm = new THREE.ShaderMaterial({
      uniforms: Object.assign({}, common, { uRes: flareRes }),
      vertexShader: ARC_GLSL + FLARE_VS, fragmentShader: NOISE_GLSL + FLARE_FS,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
    });
    const ribbons = new THREE.Mesh(rg, rm);
    // sparks streaming along the loops
    const ns = n * SPK;
    const sPos = new Float32Array(ns * 3), sA = new Float32Array(ns * 3), sB = new Float32Array(ns * 3);
    const sP = new Float32Array(ns * 4), sS = new Float32Array(ns);
    loops.forEach((lp, i) => {
      for (let k = 0; k < SPK; k++) {
        const j = i * SPK + k;
        sA.set([lp.A.x, lp.A.y, lp.A.z], j * 3); sB.set([lp.B.x, lp.B.y, lp.B.z], j * 3);
        sP.set(lp.P, j * 4); sS[j] = fr();
      }
    });
    const sg = new THREE.BufferGeometry();
    sg.setAttribute('position', new THREE.BufferAttribute(sPos, 3));
    sg.setAttribute('aA', new THREE.BufferAttribute(sA, 3));
    sg.setAttribute('aB', new THREE.BufferAttribute(sB, 3));
    sg.setAttribute('aP', new THREE.BufferAttribute(sP, 4));
    sg.setAttribute('aS', new THREE.BufferAttribute(sS, 1));
    const sm = new THREE.ShaderMaterial({
      uniforms: Object.assign({}, common, { uTime: { value: 0 }, uIntro: { value: 0 }, uScale: { value: emberScale }, uSize: { value: 0.011 } }),
      vertexShader: ARC_GLSL + SPARK_VS, fragmentShader: SPARK_FS,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    const sparks = new THREE.Points(sg, sm);
    const group = new THREE.Group();
    for (const o of [ribbons, sparks]) { o.frustumCulled = false; o.renderOrder = 7; group.add(o); }
    group.visible = false;
    spinG.add(group);
    disposables.push(rg, rm, sg, sm);
    flares = { group, mats: [rm, sm] };
  }
  if (!useTexture) makeEmbers(new THREE.Vector3(-0.5, -0.45, 0.74).normalize(), 0.18 + 0.3 * opt.lava);

  /* ---------------- clouds and atmosphere (generated once on the GPU, then just rotated) */
  let clouds = null;
  if (opt.clouds && (opt.cloudAmount > 0 || opt.atmosphere > 0)) {
    const size = coarse ? 384 : 512;
    const rt = new THREE.WebGLCubeRenderTarget(size, { generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false });
    const cam = new THREE.CubeCamera(0.1, 10, rt);
    const bm = new THREE.ShaderMaterial({
      uniforms: { uSeed: { value: new THREE.Vector3(seedRand() * 50, seedRand() * 50, seedRand() * 50) }, uAmount: { value: opt.cloudAmount } },
      vertexShader: 'varying vec3 vP; void main() { vP = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: NOISE_GLSL + CLOUD_BAKE_FS,
      side: THREE.BackSide, depthTest: false, depthWrite: false,
    });
    const bs = new THREE.Scene();
    const bb = new THREE.Mesh(new THREE.BoxGeometry(2, 2, 2), bm);
    bs.add(bb);
    const premult = {
      transparent: true, depthWrite: false, blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor,
      blendSrcAlpha: THREE.OneFactor, blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
    };
    const shellMat = new THREE.ShaderMaterial(Object.assign({
      uniforms: {
        tClouds: { value: rt.texture }, uOpacity: { value: Math.min(1, opt.cloudAmount * 1.6) }, uAtmo: { value: opt.atmosphere }, uIntro: { value: 0 },
        uLightDir: sun.uLightDir, uWrap: sun.uWrap, uRimBase: sun.uRimBase,
      },
      vertexShader: CLOUD_VS,
      fragmentShader: CLOUD_FS,
    }, premult));
    const shellGeo = new THREE.SphereGeometry(1.012, coarse ? 96 : 160, coarse ? 64 : 96);
    const shell = new THREE.Mesh(shellGeo, shellMat);
    shell.renderOrder = 5;
    shell.visible = false;
    tiltG.add(shell);   // tilts with the planet, spins on its own
    const haloMat = new THREE.ShaderMaterial(Object.assign({
      uniforms: { uR: { value: 1 }, uAtmo: { value: opt.atmosphere }, uIntro: { value: 0 }, uLightDir: sun.uLightDir, uRimBase: sun.uRimBase },
      vertexShader: HALO_VS,
      fragmentShader: HALO_FS,
    }, premult));
    const haloGeo = new THREE.PlaneGeometry(2, 2);
    const halo = new THREE.Mesh(haloGeo, haloMat);
    halo.renderOrder = 6;
    halo.visible = false;
    scene.add(halo);    // a flat glow facing the camera, hidden where the planet is in front of it
    disposables.push(bm, bb.geometry, shellMat, shellGeo, haloMat, haloGeo, rt);
    clouds = { rt, cam, bs, shell, halo, baked: false, phase: 0 };
  }

  /* ---------------- sizing */
  const geom = { rPx: 100 };
  function layout() {
    const w = Math.max(1, el.clientWidth || window.innerWidth);
    const h = Math.max(1, el.clientHeight || window.innerHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    renderer.setPixelRatio(dpr);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    const rPx = (opt.size * Math.min(w, h)) / 2;
    const tanHalf = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
    const a = Math.atan(((2 * rPx) / h) * tanHalf);
    camera.position.set(0, 0, 1 / Math.sin(Math.max(a, 0.01)));
    camera.lookAt(0, 0, 0);
    camera.setViewOffset(w, h, 0, -opt.offsetY * h, w, h);
    camera.updateProjectionMatrix();
    if (clouds) {
      // radius of the planet's outline on the plane through its centre, seen from this distance
      const D = camera.position.z;
      const silR = 1 / Math.sqrt(1 - 1 / (D * D));
      clouds.halo.material.uniforms.uR.value = silR;
      clouds.halo.scale.setScalar(silR * 1.4);
    }
    emberScale = (h * dpr) / (2 * tanHalf);
    flareRes.value.set(w * dpr, h * dpr);
    if (flares) for (const m of flares.mats) m.uniforms.uScale.value = emberScale;
    geom.rPx = rPx;
    if (!useTexture) startBake(faceSizeFor(rPx * 2));
  }
  layout();
  const ro = new ResizeObserver(layout);
  ro.observe(el);

  /* ---------------- motion and dragging */
  const autoSpeed = (((reduceMotion ? 0.25 : 1) * opt.speed * 0.3) * Math.PI) / 180;
  const st = { yaw: 0, pitch: opt.tilt, vYaw: autoSpeed, vPitch: 0, dragging: false, x: 0, y: 0, t: 0, pid: null, touch: null };
  const INTERACTIVE = 'a,button,input,textarea,select,label,summary,option,[role="button"],[contenteditable],iframe,video,[data-rg-ignore]';
  const isInteractive = (t) => !!(t && t.closest && t.closest(INTERACTIVE));
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const root = document.documentElement;
  function overPlanet(x, y) {
    const r = el.getBoundingClientRect();
    return Math.hypot(x - (r.left + r.width / 2), y - (r.top + r.height / 2 + opt.offsetY * r.height)) <= geom.rPx * 1.03;
  }
  function dragStart(x, y) {
    st.dragging = true; st.x = x; st.y = y; st.t = performance.now(); st.vYaw = 0; st.vPitch = 0;
    root.classList.add('rg-grabbing');
  }
  function dragMove(x, y) {
    const now = performance.now(), dt = Math.max(8, now - st.t) / 1000;
    const dyaw = (x - st.x) / geom.rPx, dpitch = (y - st.y) / geom.rPx;
    st.yaw += dyaw;
    st.pitch = clamp(st.pitch + dpitch, -1.25, 1.25);
    st.vYaw = st.vYaw * 0.5 + (dyaw / dt) * 0.5;
    st.vPitch = st.vPitch * 0.5 + (dpitch / dt) * 0.5;
    st.x = x; st.y = y; st.t = now;
  }
  function dragEnd() {
    st.dragging = false; st.pid = null;
    root.classList.remove('rg-grabbing');
    if (performance.now() - st.t > 90) { st.vYaw = 0; st.vPitch = 0; }
  }
  const onPointerDown = (e) => {
    if (e.pointerType === 'touch' || e.button !== 0 || isInteractive(e.target) || !overPlanet(e.clientX, e.clientY)) return;
    st.pid = e.pointerId;
    dragStart(e.clientX, e.clientY);
    e.preventDefault();
  };
  const onPointerMove = (e) => {
    if (e.pointerType === 'touch') return;
    if (st.dragging) { if (e.pointerId === st.pid) dragMove(e.clientX, e.clientY); return; }
    root.classList.toggle('rg-grab', !isInteractive(e.target) && overPlanet(e.clientX, e.clientY));
  };
  const onPointerUp = (e) => { if (st.dragging && e.pointerId === st.pid) dragEnd(); };
  const onTouchStart = (e) => {
    if (st.touch && st.touch.drag) dragEnd();
    st.touch = null;
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    if (isInteractive(e.target) || !overPlanet(t.clientX, t.clientY)) return;
    st.touch = { x: t.clientX, y: t.clientY, decided: false, drag: false };
  };
  const onTouchMove = (e) => {
    const s = st.touch;
    if (!s) return;
    const t = e.touches[0];
    if (!s.decided) {
      const dx = t.clientX - s.x, dy = t.clientY - s.y;
      if (Math.hypot(dx, dy) < 6) return;
      s.decided = true;
      const pageScrolls = document.documentElement.scrollHeight > window.innerHeight + 4;
      if (!pageScrolls || Math.abs(dx) > Math.abs(dy) * 0.8) { s.drag = true; dragStart(s.x, s.y); }
      else { st.touch = null; return; }
    }
    if (s.drag) { if (e.cancelable) e.preventDefault(); dragMove(t.clientX, t.clientY); }
  };
  const onTouchEnd = () => { if (st.touch && st.touch.drag) dragEnd(); st.touch = null; };
  const listeners = opt.drag ? [
    ['pointerdown', onPointerDown], ['pointermove', onPointerMove], ['pointerup', onPointerUp], ['pointercancel', onPointerUp],
    ['touchstart', onTouchStart, { passive: true }], ['touchmove', onTouchMove, { passive: false }],
    ['touchend', onTouchEnd], ['touchcancel', onTouchEnd],
  ] : [];
  for (const [type, fn, o] of listeners) window.addEventListener(type, fn, o);

  let visible = true;
  const io = new IntersectionObserver((entries) => { visible = entries[entries.length - 1].isIntersecting; });
  io.observe(el);

  let bornAt = 0;
  let last = performance.now();
  let raf = 0;
  function tick(now) {
    raf = requestAnimationFrame(tick);
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    if (!visible) return;
    if (stepBake()) return;
    if (!ready) return;
    if (clouds && !clouds.baked) { clouds.cam.update(renderer, clouds.bs); clouds.baked = true; return; }
    if (!st.dragging) {
      st.vYaw += (autoSpeed - st.vYaw) * (1 - Math.exp(-dt * 1.2));
      st.vPitch *= Math.exp(-dt * 3);
      st.yaw += st.vYaw * dt;
      st.pitch += st.vPitch * dt;
      st.pitch += (opt.tilt - st.pitch) * (1 - Math.exp(-dt * 0.6));
      st.pitch = clamp(st.pitch, -1.25, 1.25);
    }
    spinG.rotation.y = st.yaw;
    tiltG.rotation.x = st.pitch;
    const k = Math.min(1, (now - bornAt) / 900);
    const intro = k * k * (3 - 2 * k);
    const time = reduceMotion ? 0 : now / 1000;
    planet.visible = true;
    planet.material.uniforms.uIntro.value = intro;
    planet.material.uniforms.uTime.value = time;
    if (clouds) {
      // clouds turn with the planet (including drags) plus their own slow drift
      clouds.phase += Math.max(autoSpeed, 0.02) * opt.cloudDrift * dt * (reduceMotion ? 0 : 1);
      clouds.shell.rotation.y = st.yaw + clouds.phase;
      clouds.shell.visible = opt.cloudAmount > 0 || opt.atmosphere > 0;
      clouds.halo.visible = opt.atmosphere > 0;
      clouds.shell.material.uniforms.uIntro.value = intro;
      clouds.halo.material.uniforms.uIntro.value = intro;
    }
    if (flares) {
      flares.group.visible = !reduceMotion;
      for (const m of flares.mats) { m.uniforms.uTime.value = time; m.uniforms.uIntro.value = intro; }
    }
    renderer.render(scene, camera);
  }
  raf = requestAnimationFrame(tick);

  return {
    dispose() {
      disposed = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      for (const [type, fn] of listeners) window.removeEventListener(type, fn);
      root.classList.remove('rg-grab', 'rg-grabbing');
      if (bake) { bake.rtA.dispose(); bake.rtB.dispose(); }
      for (const x of disposables) x.dispose();
      renderer.dispose();
      canvas.remove();
    },
  };
}

/* ------------------------------------------------------------------ shaders */

const NOISE_GLSL = `
  float hash13(vec3 p) { p = fract(p * 0.1031); p += dot(p, p.zyx + 31.32); return fract((p.x + p.y) * p.z); }
  vec3 hash33(vec3 p) { p = fract(p * vec3(0.1031, 0.1030, 0.0973)); p += dot(p, p.yxz + 33.33); return fract((p.xxy + p.yxx) * p.zyx); }
  float vnoise(vec3 p) {
    vec3 i = floor(p), f = fract(p);
    vec3 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
    float n000 = hash13(i), n100 = hash13(i + vec3(1, 0, 0)), n010 = hash13(i + vec3(0, 1, 0)), n110 = hash13(i + vec3(1, 1, 0));
    float n001 = hash13(i + vec3(0, 0, 1)), n101 = hash13(i + vec3(1, 0, 1)), n011 = hash13(i + vec3(0, 1, 1)), n111 = hash13(i + vec3(1, 1, 1));
    return mix(mix(mix(n000, n100, u.x), mix(n010, n110, u.x), u.y), mix(mix(n001, n101, u.x), mix(n011, n111, u.x), u.y), u.z) * 2.0 - 1.0;
  }
`;

// Generates the surface. Mode 0 writes albedo (sqrt-encoded) + roughness, mode 1 writes height, lava, lava glow, cavity.
const BAKE_FS = `
  uniform int uMode;
  uniform vec3 uSeed;
  uniform float uLava;
  varying vec3 vP;

  // each octave is rotated so the noise never lines up into a visible grid
  const mat3 ROT = mat3(0.00, 0.80, 0.60, -0.80, 0.36, -0.48, -0.60, -0.48, 0.64);
  float fbm(vec3 p, int oct) {
    float s = 0.0, a = 0.5;
    p = ROT * p;
    for (int i = 0; i < 6; i++) { if (i >= oct) break; s += a * vnoise(p); p = ROT * p * 2.03 + vec3(1.7, 9.2, 3.1); a *= 0.5; }
    return s;
  }
  // F1, F2 and an id for the nearest cell of a jittered 3D grid
  vec3 cells(vec3 p) {
    vec3 i = floor(p), f = fract(p);
    float f1 = 9.0, f2 = 9.0, id = 0.0;
    for (int z = -1; z <= 1; z++) for (int y = -1; y <= 1; y++) for (int x = -1; x <= 1; x++) {
      vec3 g = vec3(float(x), float(y), float(z));
      vec3 r = g + hash33(i + g) * 0.8 + 0.1 - f;
      float dd = dot(r, r);
      if (dd < f1) { f2 = f1; f1 = dd; id = hash13(i + g + 17.17); } else if (dd < f2) { f2 = dd; }
    }
    return vec3(sqrt(f1), sqrt(f2), id);
  }

  void main() {
    vec3 p = normalize(vP);
    vec3 q = p + uSeed;
    vec3 w = q + 0.26 * vec3(fbm(q * 1.6, 4), fbm(q * 1.6 + 5.2, 4), fbm(q * 1.6 + 9.7, 4));

    float lavaR = smoothstep(0.9 - uLava * 0.4, 1.0 - uLava * 0.22, dot(p, normalize(vec3(-0.5, -0.45, 0.74))) + 0.22 * fbm(q * 2.6 + 7.0, 3));
    float south = smoothstep(0.1, -0.7, p.y);   // the lower half is more solid and blocky, like the reference

    // 1. crust: large irregular islands with rounded, crumbly edges over a porous under-layer
    float blob = fbm(w * 3.2 + 21.0, 5) + 0.05 * fbm(q * 22.0, 3);
    float edgeD = blob - (-0.12 - 0.1 * south + 0.3 * lavaR);    // > 0 inside the crust
    float crust = smoothstep(0.0, 0.018, edgeD);

    // 2. cracks splitting the crust into plates; wider and blockier toward the bottom
    vec3 c = cells(w * 4.0);
    float cr = c.y - c.x + 0.03 * fbm(q * 34.0, 3);
    float crackW = mix(0.022, 0.05, south);
    float crack = 1.0 - smoothstep(crackW * 0.2, crackW * 1.3, cr);
    crust *= 1.0 - step(0.88 - 0.1 * south, c.z) * (1.0 - south * 0.6);   // a few whole plates broken away
    // fine cracks running across some plates
    vec3 c3 = cells(w * 10.0 + 7.0);
    float hair = (1.0 - smoothstep(0.0, 0.028, c3.y - c3.x + 0.025 * fbm(q * 45.0, 2)))
      * smoothstep(0.02, 0.2, fbm(q * 3.5 + 50.0, 2)) * smoothstep(-0.1, 0.15, fbm(q * 16.0 + 31.0, 2));
    float lip = 1.0 - smoothstep(0.0, 0.08, edgeD);   // thickened, crumbling plate rims

    // 3. crumbly, clinker-like under-layer: clumps of grit and gravel
    float lumps = fbm(q * 9.0 + 5.0, 4);
    vec3 g1 = cells(q * 34.0 + 3.0);
    vec3 g2 = cells(q * 80.0 + 9.0);
    float ridge = clamp((1.0 - smoothstep(0.1, 0.6, g1.x)) * (0.55 + 0.45 * g1.z) + 0.5 * (1.0 - smoothstep(0.1, 0.6, g2.x)), 0.0, 1.0);
    float grit = fbm(q * 90.0, 2);

    float hCrust = 0.74 + 0.035 * fbm(q * 5.0, 2) + 0.02 * fbm(q * 60.0, 3) + 0.02 * lip - 0.14 * crack - 0.05 * hair;
    float hBare = 0.28 + 0.32 * lumps + 0.09 * ridge + 0.05 * grit;
    float height = mix(hBare, hCrust, crust);

    // one charcoal colour throughout
    vec3 base = vec3(0.05, 0.048, 0.046);
    vec3 crustCol = base * (0.8 + 0.4 * (0.5 + fbm(q * 7.0 + 70.0, 3))) * (0.88 + 0.24 * (0.5 + grit));
    crustCol *= 1.0 - 0.8 * crack - 0.45 * hair;
    vec3 bareCol = base * (0.26 + 0.4 * ridge) * (0.8 + 0.6 * (0.5 + lumps))
      + vec3(0.01) * smoothstep(0.6, 0.95, ridge) * smoothstep(-0.05, 0.25, lumps);    // dusty tops of the grit
    vec3 albedo = mix(bareCol, crustCol, crust);
    albedo *= 1.0 - 0.5 * lavaR;
    float rough = mix(0.97, 0.84, crust);

    // lava: burning through the broken ground inside the lava region
    float fire = lavaR * (1.0 - 0.9 * crust);
    // flame-like: stretched along the planet's axis so it streaks upward
    float fn = fbm(vec3(q.x * 13.0, q.y * 8.0, q.z * 13.0) + 40.0, 5);
    float hot = smoothstep(-0.25, 0.4, fn) * (0.3 + 0.7 * smoothstep(-0.2, 0.35, fbm(q * 48.0 + 90.0, 3)));   // grainy embers
    float speck = smoothstep(0.6, 0.72, fbm(q * 26.0 + 61.0, 3) * 0.5 + 0.5 + 0.12 * fbm(q * 80.0, 2));  // ragged black cinders
    float lava = fire * hot * (1.0 - 0.9 * speck);
    lava += lavaR * crust * crack * 0.3;                                      // cracks glowing in plates near the fire
    height = mix(height, 0.08 + 0.12 * hot, fire * 0.9);
    float ao = (0.3 + 0.7 * smoothstep(0.1, 0.72, height)) * min(1.0, 0.65 + 0.35 * ridge + 0.4 * crust);

    if (uMode == 0) gl_FragColor = vec4(sqrt(clamp(albedo, 0.0, 1.0)), rough);
    else gl_FragColor = vec4(height, lava, lavaR, ao);
  }
`;

const PLANET_VS = `
  uniform samplerCube tHgt;
  uniform float uDisp;
  varying vec3 vDir;
  varying vec3 vView;
  void main() {
    vec3 d = normalize(position);
    float h = textureLod(tHgt, d, 2.5).r;
    vec4 mv = modelViewMatrix * vec4(d * (1.0 + uDisp * (h - 0.5)), 1.0);
    vDir = d;
    vView = mv.xyz;
    gl_Position = projectionMatrix * mv;
  }
`;

const PLANET_FS = `
  uniform mat3 normalMatrix;
  uniform samplerCube tAlb;
  uniform samplerCube tHgt;
  uniform float uTexel;
  uniform float uDisp;
  uniform float uBump;
  uniform float uExposure;
  uniform float uFire;
  uniform float uTime;
  uniform float uIntro;
  varying vec3 vDir;
  varying vec3 vView;

  vec3 shade(vec3 N, vec3 V, vec3 alb, float rough, vec3 L, vec3 c) {
    float NoL = dot(N, L);
    if (NoL <= 0.0) return vec3(0.0);
    vec3 H = normalize(L + V);
    float NoH = max(dot(N, H), 0.0), VoH = max(dot(V, H), 0.0), NoV = max(dot(N, V), 1e-3);
    float a = rough * rough, a2 = a * a;
    float dd = NoH * NoH * (a2 - 1.0) + 1.0;
    float D = a2 / (3.14159 * dd * dd);
    float kk = a * 0.5;
    float vis = 0.25 / ((NoV * (1.0 - kk) + kk) * (NoL * (1.0 - kk) + kk));
    float F = 0.04 + 0.96 * pow(1.0 - VoH, 5.0);
    return (alb * (1.0 - F) / 3.14159 + D * vis * F) * c * NoL;
  }

  void main() {
    vec3 d = normalize(vDir);
    vec4 A = texture(tAlb, d);
    vec4 B = texture(tHgt, d);
    vec3 alb = A.rgb * A.rgb;
    float rough = clamp(A.a, 0.08, 1.0);

    // surface normal from the height map
    vec3 up = abs(d.y) < 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    vec3 t1 = normalize(cross(up, d));
    vec3 t2 = cross(d, t1);
    float h1 = texture(tHgt, normalize(d + t1 * uTexel)).r;
    float h2 = texture(tHgt, normalize(d + t2 * uTexel)).r;
    vec3 grad = ((h1 - B.r) * t1 + (h2 - B.r) * t2) / uTexel;
    vec3 N = normalize(normalMatrix * normalize(d - uDisp * uBump * grad));
    vec3 V = normalize(-vView);
    float ao = B.a;

    // lights stay fixed to the camera: warm key top-left, cool fill right, rim from behind
    vec3 col = shade(N, V, alb, rough, normalize(vec3(-0.5, 0.72, 0.48)), vec3(1.0, 0.95, 0.88) * 3.6);
    col += shade(N, V, alb, rough, normalize(vec3(0.85, -0.15, 0.4)), vec3(0.5, 0.58, 0.72) * 0.55);
    col += shade(N, V, alb, rough, normalize(vec3(0.55, 0.45, -0.7)), vec3(0.85, 0.88, 1.0) * 1.8);
    col += alb * vec3(0.10, 0.10, 0.115) * ao;
    float NoV = max(dot(N, V), 0.0);
    col += vec3(0.03) * pow(1.0 - NoV, 4.0) * (1.0 - rough) * ao;

    // lava glow and flicker
    float lava = B.g, glow = B.b;
    if (glow > 0.002 && uFire > 0.0) {
      float fl = 0.78 + 0.32 * vnoise(d * 24.0 + vec3(0.0, uTime * 0.9, uTime * 0.37));
      vec3 lc = mix(vec3(0.35, 0.025, 0.0), vec3(1.0, 0.26, 0.015), smoothstep(0.05, 0.45, lava));
      lc = mix(lc, vec3(1.0, 0.58, 0.12), smoothstep(0.55, 0.95, lava));
      col += lc * lava * 2.4 * fl * uFire;
      col += alb * vec3(1.0, 0.32, 0.06) * glow * 3.0 * uFire * (0.65 + 0.35 * fl);
    }

    col *= uExposure;
    col = clamp((col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14), 0.0, 1.0);
    col = pow(col, vec3(1.0 / 2.2));
    gl_FragColor = vec4(col, 1.0) * uIntro;
  }
`;

// Photo-texture planet: equirectangular colour map + matching height map on a UV sphere.
// Plate tops sit at the sphere's surface; cracks and ash are pushed in, so the outline is rough too.
const TEX_VS = `
  uniform sampler2D tHgt;
  uniform float uDepth;
  varying vec2 vUv;
  varying vec3 vDir;
  varying vec3 vView;
  void main() {
    vec3 d = normalize(position);
    float h = textureLod(tHgt, uv, 3.0).r;
    float keep = 1.0 - smoothstep(0.9, 0.98, abs(d.y));   // no displacement right at the poles
    vec4 mv = modelViewMatrix * vec4(d * (1.0 - uDepth * 0.85 * keep * (1.0 - h)), 1.0);
    vUv = uv;
    vDir = d;
    vView = mv.xyz;
    gl_Position = projectionMatrix * mv;
  }
`;

const TEX_FS = `
  uniform mat3 normalMatrix;
  uniform sampler2D tMap;
  uniform sampler2D tHgt;
  uniform vec2 uHgtTexel;
  uniform vec2 uHgtSize;
  uniform float uDepth;
  uniform float uPomMax;
  uniform float uPomMin;
  uniform float uShadowSteps;
  uniform float uExposure;
  uniform float uFire;
  uniform float uTime;
  uniform float uIntro;
  varying vec2 vUv;
  varying vec3 vDir;
  varying vec3 vView;

  uniform float uPulse;
  uniform vec3 uLightDir;
  uniform float uKey;
  uniform float uFill;
  uniform float uAmbient;
  uniform float uLavaGain;
  // lava = clearly red, or a white-hot core with any red tint (the rock itself is graded to neutral grey)
  float fireMask(vec3 c) {
    float red = smoothstep(0.12, 0.32, c.r - c.b) * smoothstep(0.25, 0.55, c.r);
    float core = smoothstep(0.04, 0.12, c.r - c.b) * smoothstep(0.6, 0.85, c.r);
    return max(red, core);
  }
  // molten rock colour by temperature: deep red -> red -> orange -> yellow-hot
  vec3 lavaRamp(float t) {
    t = clamp(t, 0.0, 1.4);
    vec3 c = mix(vec3(0.3, 0.01, 0.0), vec3(1.0, 0.07, 0.0), smoothstep(0.0, 0.35, t));
    c = mix(c, vec3(1.0, 0.36, 0.03), smoothstep(0.3, 0.75, t));
    return mix(c, vec3(1.0, 0.75, 0.3), smoothstep(0.7, 1.25, t));
  }
  float H(vec2 uv, float lod) { return textureLod(tHgt, uv, lod).r; }
  // arc length on the unit sphere -> equirectangular texture coordinates
  vec2 arcToUv(vec2 a, float sinT) { return vec2(a.x / (6.28318 * sinT), a.y / 3.14159); }

  vec3 shade(vec3 N, vec3 V, vec3 alb, float rough, vec3 L, vec3 c) {
    float NoL = dot(N, L);
    if (NoL <= 0.0) return vec3(0.0);
    vec3 H = normalize(L + V);
    float NoH = max(dot(N, H), 0.0), VoH = max(dot(V, H), 0.0), NoV = max(dot(N, V), 1e-3);
    float a = rough * rough, a2 = a * a;
    float dd = NoH * NoH * (a2 - 1.0) + 1.0;
    float D = a2 / (3.14159 * dd * dd);
    float kk = a * 0.5;
    float vis = 0.25 / ((NoV * (1.0 - kk) + kk) * (NoL * (1.0 - kk) + kk));
    float F = 0.04 + 0.96 * pow(1.0 - VoH, 5.0);
    return (alb * (1.0 - F) / 3.14159 + D * vis * F) * c * NoL;
  }

  void main() {
    vec3 d = normalize(vDir);
    float sinT = max(length(d.xz), 0.08);
    vec3 T = normalize(cross(vec3(0.0, 1.0, 0.0), d) + vec3(1e-5, 0.0, 0.0));   // east
    vec3 B = cross(d, T);                                                         // north
    // view and key-light directions in this point's tangent frame (view space -> object space)
    vec3 Vv = normalize(-vView);
    vec3 Lv = normalize(uLightDir);
    vec3 Vo = Vv * normalMatrix;
    vec3 Lo = Lv * normalMatrix;
    vec3 Vts = vec3(dot(Vo, T), dot(Vo, B), dot(Vo, d));
    vec3 Lts = vec3(dot(Lo, T), dot(Lo, B), dot(Lo, d));

    // the top and bottom rows of a world map are pinched: polar caps get an unstretched patch and no relief
    float cap = smoothstep(0.88, 0.97, abs(d.y));
    float depthAmt = uDepth * (1.0 - cap);
    // where the map's left and right edges meet, u jumps from 1 to 0 between neighbouring pixels; take the
    // short way round so the texture doesn't drop to its blurriest level along that line (the dashed seam)
    vec2 gx = dFdx(vUv), gy = dFdy(vUv);
    gx.x -= floor(gx.x + 0.5);
    gy.x -= floor(gy.x + 0.5);
    vec2 gpx = gx * uHgtSize, gpy = gy * uHgtSize;
    float lod = max(0.0, 0.5 * log2(max(dot(gpx, gpx), dot(gpy, gpy))));

    // 1. parallax occlusion: walk the view ray down into the height field so crevices sink in
    vec2 uv = vUv;
    if (uPomMax > 0.5 && depthAmt > 0.0) {
      float n = floor(mix(uPomMax, uPomMin, clamp(Vts.z, 0.0, 1.0)));
      vec2 dUv = arcToUv(-Vts.xy / max(Vts.z, 0.25) * depthAmt, sinT) / n;
      float dD = 1.0 / n;
      vec2 cur = uv;
      float dep = 0.0;
      float surf = 1.0 - H(cur, lod);
      for (int i = 0; i < 32; i++) {
        if (float(i) >= n || dep >= surf) break;
        cur += dUv; dep += dD; surf = 1.0 - H(cur, lod);
      }
      vec2 prev = cur - dUv;
      float after = surf - dep;
      float before = (1.0 - H(prev, lod)) - (dep - dD);
      uv = mix(cur, prev, clamp(after / (after - before + 1e-5), 0.0, 1.0));
    }
    float h = H(uv, lod);

    vec3 c = textureGrad(tMap, uv, gx, gy).rgb;
    if (cap > 0.0) c = mix(c, textureLod(tMap, vec2(fract(0.8 + d.x * 0.08), 0.62 + d.z * 0.08), lod).rgb, cap);
    float fire = fireMask(c);

    // 2. surface normal from the height map
    float hE = H(uv + vec2(uHgtTexel.x, 0.0), lod), hW = H(uv - vec2(uHgtTexel.x, 0.0), lod);
    float hN = H(uv + vec2(0.0, uHgtTexel.y), lod), hS = H(uv - vec2(0.0, uHgtTexel.y), lod);
    float gE = (hE - hW) / (2.0 * uHgtTexel.x * 6.28318 * sinT);
    float gN = (hN - hS) / (2.0 * uHgtTexel.y * 3.14159);
    float strength = depthAmt * 1.3 * (1.0 - 0.7 * fire);
    vec3 N = normalize(normalMatrix * normalize(d - strength * (gE * T + gN * B)));

    // 3. self-shadow: march toward the key light; higher ground in the way shades the crevice
    float sh = 1.0;
    if (uShadowSteps > 0.5 && Lts.z > 0.02 && depthAmt > 0.0) {
      float n = uShadowSteps;
      vec2 sUv = arcToUv(Lts.xy / max(Lts.z, 0.12) * depthAmt * (1.0 - h), sinT) / n;
      float dH = (1.0 - h) / n;
      vec2 cur = uv;
      float rayH = h;
      float occ = 0.0;
      for (int i = 1; i <= 16; i++) {
        if (float(i) > n) break;
        cur += sUv; rayH += dH;
        occ = max(occ, (H(cur, lod) - rayH) * (1.0 - float(i) / (n + 1.0)));
      }
      sh = 1.0 - smoothstep(0.0, 0.12, occ);
    }

    // 4. cavity: recesses are darker than their surroundings
    float ao = clamp(1.0 - max(0.0, H(uv, lod + 4.0) - h) * 2.4, 0.25, 1.0);

    vec3 alb = pow(c, vec3(2.2));
    float rough = mix(0.92, 0.8, smoothstep(0.5, 0.9, h));
    // the sun lights the rock, not the lava: any bright, red-tinted pixel (lava and its pale glow) is treated
    // as dark molten rock for reflected light, so it glows on its own instead of being blown out to white
    float hotGlow = smoothstep(0.02, 0.1, c.r - c.b) * smoothstep(0.3, 0.6, max(c.r, max(c.g, c.b)));
    vec3 rockAlb = alb * (1.0 - 0.92 * max(fire, hotGlow));
    vec3 col = shade(N, Vv, rockAlb, rough, Lv, vec3(1.0, 0.96, 0.9) * uKey) * sh;
    col += shade(N, Vv, rockAlb, rough, normalize(vec3(0.85, -0.15, 0.4)), vec3(0.5, 0.58, 0.72) * 0.7 * uFill);
    col += shade(N, Vv, rockAlb, rough, normalize(vec3(0.55, 0.45, -0.7)), vec3(0.85, 0.88, 1.0) * (0.5 + 1.3 * uFill));
    col += rockAlb * vec3(uAmbient) * ao;
    col *= mix(1.0, ao, 0.6);

    // fire: the texture's own lava, self-lit and flickering. Its heat drifts and pulses in slow patches,
    // shifting between deep red, orange and yellow-hot, plus a warm glow on the ground around it.
    float fl = 0.8 + 0.3 * vnoise(d * 24.0 + vec3(0.0, uTime * 0.9, uTime * 0.37));
    float drift = vnoise(d * 5.0 + vec3(uTime * 0.13, -uTime * 0.09, uTime * 0.06));
    float ripple = vnoise(d * 18.0 + vec3(-uTime * 0.35, uTime * 0.5, 0.0));
    float breathe = sin(uTime * 1.1 + drift * 4.0);
    float heat = clamp(0.5 + uPulse * (0.38 * drift + 0.22 * ripple + 0.16 * breathe), 0.0, 1.0);
    // the pulse keeps the lava's own colour: it brightens and dims, and only the hottest cores flare toward white
    float lavaL = max(max(alb.r, alb.g), alb.b);
    vec3 hue = alb / max(lavaL, 1e-3);
    // washed-out (pale pink) lava textures get their glow pushed back to deep red; only the hottest cores stay white.
    // uLavaGain is 1 for deep-red lava, so those textures are unchanged.
    float resat = (1.0 - uLavaGain) * 2.2 * (1.0 - smoothstep(0.7, 1.0, lavaL));
    hue = clamp(mix(vec3(dot(hue, vec3(0.299, 0.587, 0.114))), hue, 1.0 + resat), 0.0, 1.0);
    hue = mix(hue, vec3(1.0, hue.g * 0.35, hue.b * 0.3), clamp(resat * 0.4, 0.0, 1.0));
    float lavaT = lavaL * (0.35 + 1.0 * heat);
    vec3 pulsed = mix(hue, vec3(1.0, 0.8, 0.65), 0.55 * smoothstep(0.75, 1.3, lavaT)) * lavaT * 3.2 * uLavaGain;
    vec3 lava = mix(alb * 3.4 * uLavaGain, pulsed, 0.8 * uPulse) * fl * uFire;
    col = mix(col, lava, fire * min(uFire, 1.0));
    vec3 near = textureLod(tMap, uv, 6.5).rgb;
    vec3 nearHue = near / max(max(near.r, near.g), max(near.b, 1e-3));
    col += alb * nearHue * fireMask(near * 1.6) * 2.2 * uFire * fl * mix(1.0, 0.7 + 0.6 * heat, uPulse);

    col *= uExposure;
    col = clamp((col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14), 0.0, 1.0);
    col = pow(col, vec3(1.0 / 2.2));
    gl_FragColor = vec4(col, 1.0) * uIntro;
  }
`;

// Cloud cover baked into a cube map: R = cloud density (wisps + thin haze). Streaked along the latitude bands.
const CLOUD_BAKE_FS = `
  uniform vec3 uSeed;
  uniform float uAmount;
  varying vec3 vP;
  const mat3 ROT = mat3(0.00, 0.80, 0.60, -0.80, 0.36, -0.48, -0.60, -0.48, 0.64);
  float fbm(vec3 p, int oct) {
    float s = 0.0, a = 0.5;
    p = ROT * p;
    for (int i = 0; i < 7; i++) { if (i >= oct) break; s += a * vnoise(p); p = ROT * p * 2.03 + vec3(1.7, 9.2, 3.1); a *= 0.5; }
    return s;
  }
  void main() {
    vec3 p = normalize(vP);
    vec3 q = vec3(p.x * 1.4, p.y * 4.2, p.z * 1.4) + uSeed;
    vec3 w = q + 0.75 * vec3(fbm(q * 1.2, 4), fbm(q * 1.2 + 5.1, 4), fbm(q * 1.2 + 9.3, 4));
    float base = fbm(w * 1.6, 6);
    float dens = smoothstep(-0.02, 0.3, base + mix(-0.25, 0.3, uAmount));
    dens *= 0.5 + 0.5 * smoothstep(-0.25, 0.3, fbm(w * 6.0 + 3.0, 4));      // wispy, torn edges
    float haze = 0.2 * uAmount * (0.6 + 0.4 * smoothstep(-0.3, 0.3, fbm(q * 0.8 + 21.0, 3)));
    gl_FragColor = vec4(clamp(dens + haze, 0.0, 1.0), 0.0, 0.0, 1.0);
  }
`;

const CLOUD_VS = `
  varying vec3 vDir;
  varying vec3 vN;
  varying vec3 vView;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vDir = normalize(position);
    vN = normalize(normalMatrix * normal);
    vView = mv.xyz;
    gl_Position = projectionMatrix * mv;
  }
`;

// Clouds lit by the same key light, thicker toward the edge, with a bluish atmospheric rim. Premultiplied alpha.
const CLOUD_FS = `
  uniform samplerCube tClouds;
  uniform float uOpacity;
  uniform float uAtmo;
  uniform float uIntro;
  uniform vec3 uLightDir;
  uniform float uWrap;
  uniform float uRimBase;
  varying vec3 vDir;
  varying vec3 vN;
  varying vec3 vView;
  void main() {
    float dens = texture(tClouds, normalize(vDir)).r;
    vec3 N = normalize(vN);
    vec3 V = normalize(-vView);
    vec3 L = normalize(uLightDir);
    float lit = clamp((dot(N, L) + uWrap) / (1.0 + uWrap), 0.0, 1.0);
    float edge = 1.0 - clamp(dot(N, V), 0.0, 1.0);
    float a = clamp(dens * uOpacity * mix(1.0, 1.7, edge * edge), 0.0, 0.9);
    vec3 cloud = mix(vec3(0.02), vec3(0.92), lit * lit);
    vec3 rim = vec3(0.62) * pow(edge, 3.0) * uAtmo * (uRimBase + (1.0 - uRimBase) * lit) * 0.8;
    gl_FragColor = vec4(cloud * a + rim, a) * uIntro;
  }
`;

const HALO_VS = `
  varying vec2 vXY;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vXY = wp.xy;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

// Soft glow just outside the planet's outline, brighter on the lit side. Adds light only.
const HALO_FS = `
  uniform float uR;
  uniform float uAtmo;
  uniform float uIntro;
  uniform vec3 uLightDir;
  uniform float uRimBase;
  varying vec2 vXY;
  void main() {
    float r = length(vXY) / uR;
    float glow = r < 1.0 ? smoothstep(0.97, 1.0, r) : exp(-(r - 1.0) * 16.0);
    float toward = max(0.0, dot(normalize(vXY + 1e-5), normalize(uLightDir.xy + 1e-5)));
    float side = uRimBase + (1.0 - uRimBase) * pow(toward, 1.0 + 1.5 * (1.0 - uRimBase));
    vec3 c = vec3(0.6) * glow * side * uAtmo * 0.9;
    gl_FragColor = vec4(c, 0.0) * uIntro;
  }
`;

// Shared by the flare ribbons and sparks: where a point at t (0..1) along a loop sits at this moment.
const ARC_GLSL = `
  attribute vec3 aA;      // first foot (unit vector on the planet)
  attribute vec3 aB;      // second foot
  attribute vec4 aP;      // loop height, life period (s), phase, eruptive (0/1)
  uniform float uTime;
  float flareLife() { return fract(uTime / aP.y + aP.z); }
  float flareEnv(float life) { return smoothstep(0.0, 0.14, life) * (1.0 - smoothstep(0.6, 0.95, life)); }
  // strand: 0 = the loop's main strand, -1/1 = the fainter strands twisting either side of it
  vec3 arcPoint(float t, float life, float strand) {
    float om = acos(clamp(dot(aA, aB), -1.0, 1.0));
    vec3 base = normalize((sin((1.0 - t) * om) * aA + sin(t * om) * aB) / max(sin(om), 1e-4));
    float H = aP.x * (0.25 + 0.75 * smoothstep(0.0, 0.4, life));
    H += aP.w * aP.x * 3.2 * smoothstep(0.45, 1.0, life);          // eruptive loops lift off into space
    float bulge = sin(3.14159 * t);
    vec3 side = normalize(cross(aA, aB));
    float ph = aP.z * 40.0 + strand * 2.1;
    float wob = sin(t * 7.0 + uTime * 1.1 + ph) * 0.2 + sin(t * 17.0 - uTime * 2.3 + ph * 0.3) * 0.07;
    wob += strand * (0.07 + 0.04 * sin(t * 11.0 + uTime * 1.7 + ph));                       // strands twist round each other
    float lift = 1.0 + strand * 0.08 * sin(t * 5.0 + uTime * 0.9 + ph);
    return base * (1.004 + H * lift * pow(bulge, 0.85)) + side * wob * H * bulge;
  }
`;

const FLARE_VS = `
  attribute vec4 aT;      // t along the loop, side (-1/1), width, strand
  uniform vec2 uRes;
  uniform float uScale;
  varying float vT;
  varying float vSide;
  varying float vEnv;
  varying float vSeed;
  varying float vStrand;
  void main() {
    float life = flareLife();
    float t = aT.x;
    vec4 c0 = projectionMatrix * modelViewMatrix * vec4(arcPoint(t, life, aT.w), 1.0);
    vec4 c1 = projectionMatrix * modelViewMatrix * vec4(arcPoint(min(t + 0.02, 1.0), life, aT.w), 1.0);
    vec4 cm = projectionMatrix * modelViewMatrix * vec4(arcPoint(max(t - 0.02, 0.0), life, aT.w), 1.0);
    vec2 dir = (c1.xy / c1.w - cm.xy / cm.w) * uRes;
    float len = length(dir);
    dir = len > 1e-5 ? dir / len : vec2(1.0, 0.0);
    vec2 nrm = vec2(-dir.y, dir.x);
    float w = aT.z * (0.75 + 0.5 * (1.0 - sin(3.14159 * t))) * (1.0 + aP.w * 1.5 * smoothstep(0.45, 1.0, life));
    c0.xy += nrm * aT.y * w * uScale * 2.0 / uRes;     // screen-facing ribbon
    gl_Position = c0;
    vT = t;
    vSide = aT.y;
    vEnv = flareEnv(life);
    vSeed = aP.z + aT.w * 0.37;
    vStrand = aT.w;
  }
`;

const FLARE_FS = `
  uniform float uTime;
  uniform float uFire;
  uniform float uIntro;
  varying float vT;
  varying float vSide;
  varying float vEnv;
  varying float vSeed;
  varying float vStrand;
  uniform vec3 uTint;
  void main() {
    float flow = vnoise(vec3(vT * 16.0 - uTime * 1.8, vSeed * 31.0, uTime * 0.35));   // plasma streaming along the loop
    float knots = vnoise(vec3(vT * 42.0 + uTime * 0.7, vSeed * 7.0, 1.0));
    float tur = vnoise(vec3(vT * 26.0 - uTime * 2.6, vSide * 1.6 + vSeed * 17.0, uTime * 0.7));   // ragged, flame-like edges
    float s = abs(vSide);
    float e = s + 0.3 * tur;
    float core = exp(-e * e * 6.0);
    float glow = exp(-s * s * 1.4) * 0.3;
    float fil = clamp(0.55 + 0.35 * flow + 0.25 * knots, 0.0, 1.2);
    float feet = 1.0 + 0.6 * (1.0 - sin(3.14159 * vT));                                  // brightest where it leaves the lava
    float strandA = vStrand == 0.0 ? 1.0 : 0.5;
    float a = (core * fil * 0.75 + glow * (0.5 + 0.5 * flow)) * vEnv * feet * strandA * uFire * uIntro;
    vec3 c = mix(uTint * 0.9, mix(uTint, vec3(1.0, 0.6, 0.3), 0.4), clamp(core * fil, 0.0, 1.0));   // the lava's own colour
    c = mix(c, vec3(1.0, 0.85, 0.65), pow(core, 5.0) * smoothstep(0.85, 1.15, fil));
    gl_FragColor = vec4(c, clamp(a, 0.0, 1.0));
  }
`;

const SPARK_VS = `
  attribute float aS;
  uniform float uScale;
  uniform float uSize;
  varying float vA;
  varying float vHeat;
  void main() {
    float life = flareLife();
    float speed = 0.22 + 0.35 * fract(aS * 5.7);
    float t = fract(uTime * speed + aS);
    if (fract(aS * 13.1) > 0.5) t = 1.0 - t;              // half run each way
    vec3 p = arcPoint(t, life, fract(aS * 7.7) * 2.0 - 1.0);
    vec3 jit = (vec3(fract(aS * 91.7), fract(aS * 47.3), fract(aS * 23.9)) - 0.5) * 0.008;
    vA = flareEnv(life) * smoothstep(0.0, 0.08, t) * smoothstep(1.0, 0.92, t);
    vHeat = 0.5 + 0.5 * sin(3.14159 * t);
    vec4 mv = modelViewMatrix * vec4(p + jit, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = uSize * (0.6 + fract(aS * 3.7)) * uScale / -mv.z;
  }
`;

const SPARK_FS = `
  uniform vec3 uTint;
  uniform float uFire;
  uniform float uIntro;
  varying float vA;
  varying float vHeat;
  void main() {
    vec2 q = gl_PointCoord * 2.0 - 1.0;
    float a = exp(-dot(q, q) * 5.0) * vA * uFire * uIntro;
    vec3 c = mix(uTint, vec3(1.0, 0.8, 0.55), vHeat * 0.6);
    gl_FragColor = vec4(c, a);
  }
`;

/* ------------------------------------------------------------------ final settings (locked) */

const FINAL = {
  size: '90', offsetY: '0', yaw: '10', speed: '10', tilt: '14', drag: 'true',
  lighting: 'sun', darkSide: '0', brightness: '110',
  fire: '90', pulse: '60', flares: 'true',
  clouds: 'true', cloudAmount: '40', cloudDrift: '60', atmosphere: '60',
  lava: '50', quality: 'auto', seed: 'revenge',
  tex: new URL('planet-v6-texture-4k.jpg', import.meta.url).href,
  texMobile: new URL('planet-v6-texture-2k.jpg', import.meta.url).href,
  height: new URL('planet-v6-height-2k.jpg', import.meta.url).href,
};

function prepare() {
  if (!document.getElementById('rg-planet-v6-css')) {
    const css = document.createElement('style');
    css.id = 'rg-planet-v6-css';
    css.textContent = [
      '.rg-planet-v6 { position: relative; overflow: hidden; background: var(--rg-bg, #000); }',
      '.rg-planet-v6--background { position: fixed; inset: 0; z-index: -1; pointer-events: none; }',
      '.rg-planet-v6__canvas { display: block; width: 100%; height: 100%; }',
      'html.rg-grab, html.rg-grab body { cursor: grab; }',
      'html.rg-grabbing, html.rg-grabbing * { cursor: grabbing !important; user-select: none; -webkit-user-select: none; }',
    ].join('\n');
    document.head.appendChild(css);
  }
  let els = document.querySelectorAll('.rg-planet-v6');
  if (!els.length) {
    const el = document.createElement('div');
    el.className = 'rg-planet-v6 rg-planet-v6--background';
    document.body.prepend(el);
    els = [el];
  }
  for (const el of els) {
    el.style.setProperty('--rg-bg', '#000000');
    for (const k of Object.keys(FINAL)) el.dataset[k] = FINAL[k];   // always the locked values
  }
}

/* ------------------------------------------------------------------ start */

function start() {
  prepare();
  if (!window[NS]) window[NS] = createManager();
  window[NS].initAll(document);
}
if (document.body) start();
else document.addEventListener('DOMContentLoaded', start, { once: true });
